import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as Cesium from 'cesium';
import { API_ENDPOINTS, fetchApi } from '../../utilities/api';

interface PropertyDetails {
  name: string;
  price: number;
  location: {
    lat: number;
    lng: number;
  };
}

interface MapState {
  viewer: Cesium.Viewer | null;
  buildingTileset: Cesium.Cesium3DTileset | null;
  selectedBuilding: {
    name: string;
    price: number;
    lat: number;
    lng: number;
  } | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: MapState = {
  viewer: null,
  buildingTileset: null,
  selectedBuilding: null,
  isLoading: false,
  error: null
};

// Async thunk for fetching building details
export const fetchBuildingDetails = createAsyncThunk(
  'map/fetchBuildingDetails',
  async (elementId: string) => {
    const endpoint = API_ENDPOINTS.PROPERTY.CHECK_DETAILS(elementId);
    const response = await fetchApi<{ propertyDetails: PropertyDetails[] }>(endpoint);
    return response.data.propertyDetails[0];
  }
);

const mapSlice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    setViewer: (state, action) => {
      state.viewer = action.payload;
    },
    setBuildingTileset: (state, action) => {
      state.buildingTileset = action.payload;
    },
    setSelectedBuilding: (state, action) => {
      state.selectedBuilding = action.payload;
    },
    clearSelectedBuilding: (state) => {
      state.selectedBuilding = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBuildingDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBuildingDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.selectedBuilding = {
            name: action.payload.name || 'No Name',
            price: action.payload.price || 0,
            lat: action.payload.location?.lat || 0,
            lng: action.payload.location?.lng || 0
          };
        }
      })
      .addCase(fetchBuildingDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch building details';
      });
  }
});

export const {
  setViewer,
  setBuildingTileset,
  setSelectedBuilding,
  clearSelectedBuilding
} = mapSlice.actions;

export default mapSlice.reducer; 